import { Show } from 'react-admin';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Alert, Grid, Button, IconButton } from '@mui/material';
import { Warning, CheckCircle, Schedule, VerifiedUser, HealthAndSafety, Security, Event, Close, Group } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { STATUS_COLORS } from './constants';
import { OHSWorkflowStatus } from './OHSWorkflowStatus';
import type { OHSRepresentativeRecord } from '@/types/entities';

const RepresentativeShowContent = ({ record }: { record: OHSRepresentativeRecord | undefined }) => {
  const navigate = useNavigate();
  const [now] = useState(() => Date.now());
  if (!record) return null;
  const daysUntilExpiry = record?.termEndDate ? Math.ceil((new Date(record.termEndDate).getTime() - now) / (1000 * 60 * 60 * 24)) : null;
  const isExpiring = daysUntilExpiry !== null && daysUntilExpiry <= 60;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/ohs-representatives')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <PageDescription entity="OHS Representative" description="A designated OHS representative for a department or location." icon={<Group />} />
      {isExpired && <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>Certification expired — renewal required immediately</Alert>}
      {isExpiring && !isExpired && <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>Certification expiring in {daysUntilExpiry} days</Alert>}
      {record.status === 'Active' && !isExpiring && !isExpired && <Alert severity="success" sx={{ mb: 2 }} icon={<VerifiedUser />}>Active OHS Representative</Alert>}

      <OHSWorkflowStatus
        entityId={record.id || ''}
        entityType="representative"
        currentState={record.status}
      />

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          OHS Act Section 17 Compliance Notice
        </Typography>
        <Typography variant="body2">
          Section 17 of the Occupational Health and Safety Act requires that at least one OHS Representative
          be elected/appointed for every workplace. This representative must be trained and certified.
          Currently showing certification details for {record.employee?.id}.
        </Typography>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Representative', value: record.employee?.id, bold: true },
          { label: 'Employee ID', value: record.employee?.id },
          { label: 'Area Represented', value: record.areaRepresented },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Training Completed', value: record.trainingCompleted ? 'Yes' : 'No - Training Required' },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Date Elected</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.electedAt ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Term End Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.termEndDate ?? '—'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>OHS Representative Duties</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                'Conduct workplace inspections',
                'Investigate incidents and near misses',
                'Report unsafe conditions',
                'Participate in OHS committee',
                'Assist with emergency procedures',
              ].map((duty, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" fontSize="small" />
                  <Typography variant="body2">{duty}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button component={Link} to="/workplace-injuries" variant="outlined" size="small" startIcon={<Warning />}>
                View Incidents
              </Button>
              <Button component={Link} to="/ohs-certifications" variant="outlined" size="small" startIcon={<HealthAndSafety />}>
                View All Certifications
              </Button>
              <Button component={Link} to="/ohsCalendar" variant="outlined" size="small" startIcon={<Event />}>
                View OHS Calendar
              </Button>
              <Button component={Link} to="/employees" variant="outlined" size="small">
                View Employee
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export const RepresentativeShow = () => (
  <Show render={(props) => {
    const record = props.record as OHSRepresentativeRecord | undefined;
    return <RepresentativeShowContent record={record} />;
  }} />
);
